import { XMarkIcon, ChevronDownIcon } from '@heroicons/react/20/solid'
import React, { JSX, useEffect, useMemo, useState } from 'react'
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react'
import { useMediaQuery } from 'react-responsive'

export interface DropdownType {
  id: string
  title: string
}

export interface MultiSelectComboboxProps {
  testId: string
  data: DropdownType[]
  selected: DropdownType[]
  setSelected: (value: DropdownType[]) => void
  type?: string
  allOption?: boolean
  disabled: boolean
}

export const MultiSelectDropdown = ({
  testId,
  data,
  selected,
  setSelected,
  type = 'Product',
  allOption = false,
  disabled = false
}: MultiSelectComboboxProps): JSX.Element => {
  const [query, setQuery] = useState('')
  const allItem: DropdownType = { id: 'all', title: 'All' }
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => a.title.localeCompare(b.title))
  }, [data])
  const fullData = allOption ? [allItem, ...sortedData] : sortedData
  const [label, setLabel] = useState('All')
  const isMobile = useMediaQuery({ maxWidth: 639 })
  const [open, setOpen] = useState(false)
  const [isTyping, setIsTyping] = useState(false)

  const filteredData = useMemo(() => {
    return fullData.filter((item) =>
      item.title.toLowerCase().includes(query.toLowerCase())
    )
  }, [query, fullData])

  const isSelected = (item: DropdownType): boolean =>
    Array.isArray(selected) && selected.some((s) => s.id === item.id)

  const getNewSelection = (item: DropdownType, current: DropdownType[]): DropdownType[] => {
    const isAll = item.id === 'all'
    const isAllSelected = current.some(s => s.id === 'all')
    const alreadySelected = current.some(s => s.id === item.id)
    const wouldSelectAll = current.length + 1 === data.length

    const selectSingle = (): DropdownType[] => [item]
    const removeItem = (): DropdownType[] => current.filter(s => s.id !== item.id)
    const selectAll = (): DropdownType[] => [allItem]
    const addItem = (): DropdownType[] => [...current, item]

    if (isAll || isAllSelected) return selectSingle()
    if (alreadySelected) {
      if (current.length === 1) {
        return selectAll()
      }
      return removeItem()
    }
    if (wouldSelectAll) return selectAll()
    return addItem()
  }

  const toggleSelection = (item: DropdownType): void => {
    setSelected(getNewSelection(item, selected))
    setOpen(false)
    setQuery('')
    setIsTyping(false)
  }

  const getLabel = (selected: DropdownType[], type: string): string => {
    const isAllSelected = selected[0]?.id === 'all'
    const count = selected.length
    const pluralType = count === 1 ? type : `${type}s`
    return isAllSelected || count === 0 ? 'All' : `(${count}) ${pluralType}`
  }

  useEffect(() => {
    setLabel(getLabel(selected, type))
  }, [selected, type])

  const noResults = (
    <div className='px-4 py-2 dark:text-gray-300'>No results found</div>
  )

  const createOption = (item: DropdownType): JSX.Element => (
    <ComboboxOption
      key={item.id}
      value={item}
      as='li'
      data-testid={`${testId}-option-${item.id}`}
      className={`
    flex items-center justify-between px-4 py-2 cursor-default select-none
    hover:bg-lm-bg-accent dark:hover:bg-dm-bg-accent ui-active:bg-lm-bg-secondary dark:ui-active:bg-dm-bg-secondary
    ${isSelected(item) ? 'bg-lm-bg-secondary dark:bg-dm-bg-secondary' : ''}
  `}
      onClick={() => toggleSelection(item)}
    >
      <span>{item.title}</span>
      {isSelected(item) && item.id !== 'all' && (
        <XMarkIcon className='w-4 h-4 min-w-[1rem] min-h-[1rem] text-primary flex-shrink-0' />
      )}
    </ComboboxOption>
  )

  const dropdownOptions = filteredData.length === 0 && query !== ''
    ? noResults
    : filteredData.map(createOption)

  const disabledClass = disabled ? 'pointer-events-none opacity-50' : ''
  return (
    <Combobox data-testid={`combobox-${type}`} value={selected ?? []} onChange={() => {}} multiple disabled={disabled}>
      <div className={`relative ${disabledClass}`}>
        <div className='relative w-full'>
          <ComboboxInput
            onFocus={() => {
              setIsTyping(true)
            }}
            onBlur={() => {
              setIsTyping(false)
              setQuery('')
              setOpen(false)
            }}
            onChange={(e) => {
              setQuery(e.target.value)
              setOpen(true)
            }}
            displayValue={() => (isTyping ? query : label)}
            autoComplete='off'
            autoCorrect='off'
            spellCheck='false'
            name='input-for-multi'
            readOnly={disabled || isMobile}
            disabled={disabled}
            data-testid={`${testId}-input`}
            className='w-full rounded-md px-3 py-2 pr-10 text-sm font-semibold ring-1 shadow-xs dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 ring-black/5 ring-inset focus:outline-none focus:ring-2 focus:ring-primary placeholder-black dark:placeholder-white text-ellipsis'
            placeholder={label}
          />
          <ComboboxButton
            data-testid={`${testId}-trigger`}
            className='absolute inset-y-0 right-0 flex items-center pr-2'
            onClick={() => setOpen(!open)}
          >
            <ChevronDownIcon
              className='h-5 w-5 text-lm-text-secondary dark:text-dm-text-secondary'
              aria-hidden='true'
            />
          </ComboboxButton>
        </div>

        {!disabled && open && (
          <ComboboxOptions data-testid={`${testId}-options`} static className='absolute z-10 mt-2 w-full origin-top-right rounded-md bg-lm-bg-main dark:bg-dm-bg-main ring-1 shadow-lg dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 ring-black/5 max-h-60 overflow-auto focus:outline-none text-sm'>
            {dropdownOptions}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  )
}
